import { Box, Button, Paper, Slide, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { initAnalytics, isAnalyticsConfigured } from "../../services/analytics";
import { CONSENT, hasResponded, setConsent } from "../../services/consent";

/**
 * GDPR/PECR analytics consent banner.
 *
 * Analytics stays off until the user explicitly accepts. The banner only
 * appears when analytics is actually configured (a Measurement ID is set) and
 * the user hasn't chosen yet. A `open-consent` window event re-opens it, so a
 * settings/privacy page can let users change their mind later.
 */
export default function ConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isAnalyticsConfigured() && !hasResponded()) setOpen(true);

    const reopen = () => setOpen(true);
    window.addEventListener("open-consent", reopen);
    return () => window.removeEventListener("open-consent", reopen);
  }, []);

  const accept = () => {
    setConsent(CONSENT.GRANTED);
    initAnalytics(); // start collecting now that consent is given
    setOpen(false);
  };

  const decline = () => {
    setConsent(CONSENT.DENIED);
    setOpen(false);
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        square
        elevation={8}
        role="dialog"
        aria-label="Analytics consent"
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1500,
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            We use analytics to improve FindASide
          </Typography>
          <Typography variant="caption" color="text.secondary">
            We'd like to use analytics cookies to understand how the app is used
            so we can make it better. Declining means no analytics data is
            collected — the app works exactly the same.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexShrink: 0,
            justifyContent: { xs: "flex-end", sm: "initial" },
          }}
        >
          <Button size="small" color="inherit" onClick={decline}>
            Decline
          </Button>
          <Button size="small" variant="contained" onClick={accept}>
            Accept
          </Button>
        </Box>
      </Paper>
    </Slide>
  );
}
